// The discharge-accuracy API, and the pure mapping from what the server sends
// to what the screen renders.
//
// Every label the template shows is computed here rather than in the template
// or a pipe: the screen is OnPush, so a row it can compare by reference is
// worth more than a template that recomputes a date on every check - and these
// rules (what counts as on time, what a step in the trail says) are the ones
// worth testing without a component around them.

import { Injectable } from '@angular/core';
import { defer, from, Observable, of } from 'rxjs';
import { catchError, map, switchMap, timeout } from 'rxjs/operators';
import { ServerTokenService } from '../../services/server-token.service';
import { FacilityService } from '../../manageb/facility.service';
import {
  AccuracyChangeDto,
  AccuracyChangeView,
  AccuracyFacilityChip,
  AccuracyFacilityTotalsDto,
  AccuracyFigure,
  AccuracyLead,
  AccuracyOutcome,
  AccuracyReportDto,
  AccuracyReportView,
  AccuracyRowDto,
  AccuracyRowView,
  AccuracySummaryDto,
  AccuracyTotalsDto,
  AccuracyTrailStep,
  AccuracyWindow,
} from './discharge-accuracy.interface';

/** How long the report waits for the facility registry before loading
 *  anyway. The registry names the chips and the rows; a registry that never
 *  settles must not hold the report hostage, and the names then fall back to
 *  the ids the way facilityNameOf already does. */
const REGISTRY_TIMEOUT_MS = 10000;

/** A stay is "on time" when it ended within a day either side of the last date
 *  written. One day is the tolerance a discharge plan is actually held to -
 *  transport, a signature and a ride home all move a discharge by a day. */
export const ON_TIME_DAYS = 1;

const EMPTY_SUMMARY: AccuracySummaryDto = {
  stays: 0, everPlanned: 0, plannedAhead: 0, plannedLate: 0, plannedWrong: 0, surprises: 0, averageNoticeDays: null,
  measured: 0, withinOneDay: 0, averageDaysOff: null, everMoved: 0, totalMoves: 0,
};

/** No stays at all. A building in the reader's access with no discharge in
 *  the window still gets a chip, and this is what it adds up to. */
export const EMPTY_TOTALS: AccuracyTotalsDto = {
  stays: 0, everPlanned: 0, plannedAhead: 0, plannedLate: 0, plannedWrong: 0, noticeTotal: 0,
  measured: 0, withinOneDay: 0, absDaysOffTotal: 0, everMoved: 0, totalMoves: 0,
};

/**
 * Addition, and only addition. The server decided every count per row - what
 * the board showed, what is measurable, what landed within a day - and the
 * page adds the buildings the reader ticked. Pure; exported for the tests.
 */
export function mergeTotals(list: ReadonlyArray<AccuracyTotalsDto | null | undefined>): AccuracyTotalsDto {
  return list.reduce<AccuracyTotalsDto>((sum, t) => ({
    stays: sum.stays + (t?.stays ?? 0),
    everPlanned: sum.everPlanned + (t?.everPlanned ?? 0),
    plannedAhead: sum.plannedAhead + (t?.plannedAhead ?? 0),
    plannedLate: sum.plannedLate + (t?.plannedLate ?? 0),
    plannedWrong: sum.plannedWrong + (t?.plannedWrong ?? 0),
    noticeTotal: sum.noticeTotal + (t?.noticeTotal ?? 0),
    measured: sum.measured + (t?.measured ?? 0),
    withinOneDay: sum.withinOneDay + (t?.withinOneDay ?? 0),
    absDaysOffTotal: sum.absDaysOffTotal + (t?.absDaysOffTotal ?? 0),
    everMoved: sum.everMoved + (t?.everMoved ?? 0),
    totalMoves: sum.totalMoves + (t?.totalMoves ?? 0),
  }), EMPTY_TOTALS);
}

/**
 * Division, and only division: the same arithmetic the server applies to the
 * same counts (summaryFromTotals), so a selection of every facility reads
 * exactly what the server's own summary says, and a selection of three reads
 * what the server would have said for those three. Pure; exported for the
 * tests.
 */
export function summaryOfTotals(t: AccuracyTotalsDto): AccuracySummaryDto {
  return {
    stays: t.stays,
    everPlanned: t.everPlanned,
    plannedAhead: t.plannedAhead,
    plannedLate: t.plannedLate,
    plannedWrong: t.plannedWrong,
    surprises: t.stays - t.plannedAhead,
    averageNoticeDays: t.plannedAhead ? Math.round((t.noticeTotal / t.plannedAhead) * 10) / 10 : null,
    measured: t.measured,
    withinOneDay: t.withinOneDay,
    averageDaysOff: t.measured ? Math.round((t.absDaysOffTotal / t.measured) * 10) / 10 : null,
    everMoved: t.everMoved,
    totalMoves: t.totalMoves,
  };
}

/** A date-only string as a UTC calendar date. The whole feature stores naive
 *  facility-local days, so this must not convert zones - the server's readers
 *  carry the same warning. */
function utcDayOf(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  const parsed = Date.parse(`${iso}T00:00:00.000Z`);
  if (Number.isNaN(parsed)) return null;
  const date = new Date(parsed);
  return date.toISOString().slice(0, 10) === iso ? date : null;
}

/** "9/24" - the compact form for the trail, where four or five of them sit in
 *  a row and the year is the window's, not news. */
export function shortDay(iso: string | null | undefined): string {
  const date = utcDayOf(iso);
  if (!date) return '';
  return `${date.getUTCMonth() + 1}/${date.getUTCDate()}`;
}

/** "9/24/2026" - the full form, for the log, where each line is read once. */
export function fullDay(iso: string | null | undefined): string {
  const date = utcDayOf(iso);
  if (!date) return iso || '';
  return `${date.getUTCMonth() + 1}/${date.getUTCDate()}/${date.getUTCFullYear()}`;
}

/** The zone every timestamp on this screen is rendered in: the facilities'
 *  own, the same one the Discharge Plan screen and the bed board stamp "Set
 *  on" with (easternDateText) and the one the server measures the notice in.
 *  A reader in another zone must see the day the server counted from, or the
 *  "Documented on" column and the notice beside it disagree around midnight. */
const EASTERN_ZONE = 'America/New_York';

interface EasternClock {
  month: string;
  day: string;
  hour: string;
  minute: string;
}

/** Built ONCE. Resolving a locale and a zone is the expensive half of
 *  Intl.DateTimeFormat; formatting with one already built is cheap. This runs
 *  for every change of every row the moment a report lands - a few thousand
 *  times on a busy window - and a formatter per call was a visible freeze
 *  behind the loading state. The instance holds no state, so it is shared. */
const EASTERN_FORMATTER = new Intl.DateTimeFormat('en-US', {
  timeZone: EASTERN_ZONE, month: 'numeric', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: false,
});

/** A real instant, broken into its Eastern calendar day and clock. null for
 *  a timestamp that cannot be parsed. */
function easternClockOf(iso: string): EasternClock | null {
  const parsed = Date.parse(iso || '');
  if (Number.isNaN(parsed)) return null;
  const parts = EASTERN_FORMATTER.formatToParts(new Date(parsed));
  const part = (type: string): string => parts.find(p => p.type === type)?.value ?? '';
  // Unpadded, and midnight is "0": some engines print it as "24" under
  // hour12: false, and others pad every hour to two digits.
  const hour = String(Number(part('hour')) % 24);
  return { month: part('month'), day: part('day'), hour, minute: part('minute') };
}

/** "9/14, 10:02" - when a save happened, on the Eastern clock. */
export function stampOf(iso: string): string {
  const clock = easternClockOf(iso);
  if (!clock) return '';
  return `${clock.month}/${clock.day}, ${clock.hour}:${clock.minute}`;
}

/** "9/14" from a real timestamp: the Eastern day it landed on, which is the
 *  day the server measured the notice from. Distinct from shortDay, which
 *  reads a naive calendar day and must not convert zones. */
export function dayOfStamp(iso: string): string {
  const clock = easternClockOf(iso);
  if (!clock) return '';
  return `${clock.month}/${clock.day}`;
}

export function outcomeOf(varianceDays: number | null, actualDischargeDate: string | null): AccuracyOutcome {
  if (actualDischargeDate === null || varianceDays === null) return 'open';
  if (Math.abs(varianceDays) <= ON_TIME_DAYS) return 'onTime';
  return varianceDays > 0 ? 'late' : 'early';
}

/** "+6" / "-3" / "0", or an em dash while the stay is open. The sign is the
 *  point: it says whether the patient stayed longer or left sooner. */
export function varianceLabelOf(varianceDays: number | null): string {
  if (varianceDays === null) return '—';
  if (varianceDays === 0) return '0';
  return varianceDays > 0 ? `+${varianceDays}` : String(varianceDays);
}

const LEADS: ReadonlyArray<AccuracyLead> = ['ahead', 'late', 'wrong', 'none'];

/** The server's reading, taken as sent. The rule - what the board showed two
 *  days out - lives in one place, and a client that re-derived it would drift
 *  from the figures in the band above. A reading this screen does not know,
 *  or none at all, reads as 'none' - the same fallback the server's own guard
 *  applies - so a row never claims more than the server did. Pure; exported
 *  for the tests. */
export function leadOf(lead: unknown): AccuracyLead {
  return typeof lead === 'string' && LEADS.indexOf(lead as AccuracyLead) !== -1 ? (lead as AccuracyLead) : 'none';
}

/** The pill's words. "4 days ahead" rather than "4", because the column is
 *  read by someone deciding whether a case manager is on top of their list;
 *  "Board said 9/27" rather than "wrong", because the date IS the finding. */
export function leadLabelOf(lead: AccuracyLead, noticeDays: number | null, boardDate: string | null): string {
  switch (lead) {
    case 'ahead':
      if (noticeDays === null) return 'Anticipated';
      return noticeDays === 1 ? '1 day ahead' : `${noticeDays} days ahead`;
    case 'late': return 'Late plan';
    case 'wrong': {
      const said = shortDay(boardDate);
      return said ? `Planned for ${said}` : 'Incorrect date';
    }
    default: return 'No plan';
  }
}

/** "27 days", or empty when the mirror holds no admission day. Empty rather
 *  than a zero: an unknown stay length is not a stay of no length. */
export function stayLabelOf(lengthOfStayDays: number | null): string {
  if (lengthOfStayDays === null) return '';
  return lengthOfStayDays === 1 ? '1 day' : `${lengthOfStayDays} days`;
}

/**
 * The distinct dates that were written, in order.
 *
 * Built from the changes rather than from first/last, so the reader sees the
 * actual path - 9/8 to 9/12 to 9/18 - and not just its ends. A save that only
 * edited the note adds no step: its two dates are equal, and a trail that
 * repeated the same date would report a move that never happened.
 */
export function trailOf(changes: AccuracyChangeDto[]): AccuracyTrailStep[] {
  const steps: AccuracyTrailStep[] = [];
  for (const change of changes ?? []) {
    if (change?.to === null || change?.to === undefined) continue;
    if (change.to === change.from) continue;
    steps.push({ label: shortDay(change.to) });
  }
  return steps;
}

/** What one save did, in words. */
export function changeTextOf(change: AccuracyChangeDto): string {
  if (change?.to === null || change?.to === undefined) return 'Plan cleared';
  if (change.from === null || change.from === undefined) return `First plan ${fullDay(change.to)}`;
  if (change.from === change.to) return `Note edited, ${fullDay(change.to)} kept`;
  return `${fullDay(change.from)} to ${fullDay(change.to)}`;
}

/** The log lines. Every event carries the note in force after it, so a plan
 *  moved five times under one note would print that note five times; the
 *  note is shown on the line where it first appears or changes, and left off
 *  the lines that merely carried it forward. Pure; exported for the tests. */
export function changeViewsOf(changes: ReadonlyArray<AccuracyChangeDto>): AccuracyChangeView[] {
  return changes.map((change, i) => {
    const note = change?.note || '';
    const carried = i > 0 && (changes[i - 1]?.note || '') === note;
    return {
      when: stampOf(change?.at),
      what: changeTextOf(change),
      by: change?.by || '',
      note: carried ? '' : note,
    };
  });
}

/**
 * The band that answers the question the report was asked: of the discharges
 * that happened, how many did the board show coming two days out - and the
 * three ways it did not, which add up to the surprises.
 *
 * Quoted out of `stays` - every discharge in the window - because that is the
 * denominator that makes the number mean something. Quoting coverage out of
 * the stays that HAD a plan would report 100% forever.
 */
/** One building's rows, for the by-facility layout. */
export interface AccuracyRowGroup {
  facilityId: number;
  name: string;
  rows: AccuracyRowView[];
}

/** The rows gathered by building, buildings in name order, each building's
 *  rows in the order they arrived (the chosen sort). Pure; exported for the
 *  tests. */
export function groupRowsByFacility(rows: ReadonlyArray<AccuracyRowView>): AccuracyRowGroup[] {
  const byFacility = new Map<number, AccuracyRowGroup>();
  for (const row of rows) {
    const group = byFacility.get(row.facilityId);
    if (group) group.rows.push(row);
    else byFacility.set(row.facilityId, { facilityId: row.facilityId, name: row.facilityName || '', rows: [row] });
  }
  return [...byFacility.values()].sort((a, b) => a.name.localeCompare(b.name));
}

/** The band with nothing ticked. An empty pick is a choice, not a finding, so
 *  the figures go blank and say why, rather than reading "no discharges"
 *  above an empty state that says "no facility". */
export function blankFigures(figures: ReadonlyArray<AccuracyFigure>): AccuracyFigure[] {
  return figures.map(figure => ({ ...figure, value: '—', sub: 'No facility selected', tone: 'plain' as const }));
}

export function leadFiguresOf(summary: AccuracySummaryDto): AccuracyFigure[] {
  const stays = summary.stays;
  const aheadShare = stays ? Math.round((summary.plannedAhead / stays) * 100) : null;
  // `?? 0` for a server that predates the split: a tile must never print NaN.
  const late = summary.plannedLate ?? 0;
  const wrong = summary.plannedWrong ?? 0;
  const missed = late + wrong;
  const noPlan = stays - summary.everPlanned;
  // The sub-lines are visible under the figures, not tooltips: short, and
  // together they read as one sentence - so many discharges, so many seen,
  // so many not, and why not.
  return [
    {
      key: 'stays',
      label: 'Discharges',
      value: String(stays),
      sub: 'in this window',
      tone: 'plain',
    },
    {
      key: 'plannedAhead',
      label: 'Anticipated',
      value: aheadShare === null ? '—' : `${aheadShare}%`,
      sub: stays ? `${summary.plannedAhead} of ${stays}, 2 days ahead` : 'No discharges in this window',
      tone: 'good',
    },
    {
      key: 'surprises',
      label: 'Unanticipated',
      value: String(summary.surprises),
      sub: 'not anticipated 2 days ahead',
      tone: 'warn',
    },
    {
      key: 'missed',
      label: 'Late or incorrect plan',
      value: String(missed),
      sub: `${late} late · ${wrong} incorrect date`,
      tone: 'plain',
    },
    {
      key: 'noPlan',
      label: 'No plan',
      value: String(noPlan),
      sub: 'no date documented',
      tone: 'plain',
    },
    {
      key: 'lead',
      label: 'Advance notice',
      value: summary.averageNoticeDays === null ? '—' : `${summary.averageNoticeDays}d`,
      sub: 'average, anticipated stays',
      tone: 'plain',
    },
  ];
}

/** The second band. Accuracy is quoted out of the stays that have actually
 *  ENDED and had a date to be wrong about, and the sub-line says so - a
 *  percentage out of everyone would drift for no reason and read as a
 *  facility getting worse. */
export function figuresOf(summary: AccuracySummaryDto): AccuracyFigure[] {
  const measured = summary.measured;
  const onTimeShare = measured ? Math.round((summary.withinOneDay / measured) * 100) : null;
  const movedShare = summary.stays ? Math.round((summary.everMoved / summary.stays) * 100) : null;
  return [
    {
      key: 'onTime',
      label: 'Discharged within a day',
      value: onTimeShare === null ? '—' : `${onTimeShare}%`,
      sub: measured
        ? `${summary.withinOneDay} of ${measured} with a date`
        : 'No stay has ended yet',
      tone: 'good',
    },
    {
      key: 'daysOff',
      label: 'Average variance',
      value: summary.averageDaysOff === null ? '—' : String(summary.averageDaysOff),
      sub: 'actual vs planned, in days',
      tone: 'plain',
    },
    {
      key: 'moved',
      label: 'Dates that moved',
      value: movedShare === null ? '—' : `${movedShare}%`,
      sub: summary.stays ? `${summary.everMoved} of ${summary.stays} stays` : 'No stays in this window',
      tone: 'warn',
    },
    {
      key: 'changes',
      label: 'Date changes',
      value: String(summary.totalMoves),
      sub: 'in this window',
      tone: 'plain',
    },
  ];
}

/** The columns the comparison strip can be ordered by. */
export type FacilityCompareKey = 'facility' | 'stays' | 'plannedAhead' | 'notice' | 'surprises';

/**
 * One building's line in the comparison strip: the headline figures of the
 * lead band, for THIS building alone, beside how many discharges they are out
 * of, and the three shares the bar is drawn from. Shares are kept raw for the
 * sort and the bar, and rendered once for the screen.
 */
export interface FacilityCompareRow {
  facilityId: number;
  name: string;
  stays: number;
  /** plannedAhead / stays, or null with no stays. */
  plannedAheadShare: number | null;
  plannedAheadLabel: string;
  /** The bar's three segments, as percentages that sum to 100 for a building
   *  with stays and to 0 for one without: on the board, had a date but not on
   *  the board two days out, never a date. */
  aheadPercent: number;
  missedPercent: number;
  noPlanPercent: number;
  /** The bar in words, for the tooltip and the screen reader. */
  barLabel: string;
  averageNoticeDays: number | null;
  noticeLabel: string;
  surprises: number;
  /** Ticked in the chips. An unticked building stays on the strip, dimmed:
   *  the comparison is only a comparison while every building is on it. */
  selected: boolean;
}

/** "62%" out of a building's own discharges, or a dash for a building with
 *  none - the same rounding as the band above it. */
function shareLabel(share: number | null): string {
  return share === null ? '—' : `${Math.round(share * 100)}%`;
}

/** A count as a percentage of the stays, to two decimals, 0 with none. The bar
 *  widths are drawn from these, and three whole numbers left a sliver of track
 *  showing (33 + 33 + 33); two decimals fill it. */
function percentOf(count: number, stays: number): number {
  return stays ? Math.round((count / stays) * 10000) / 100 : 0;
}

/** The strip's lines from the chips: each building's counts divided on their
 *  own, the server's arithmetic. Pure; exported for the tests. */
export function compareRowsOf(chips: ReadonlyArray<AccuracyFacilityChip>, selected: ReadonlySet<number>): FacilityCompareRow[] {
  return chips.map(chip => {
    const summary = summaryOfTotals(chip.totals);
    const stays = summary.stays;
    const plannedAheadShare = stays ? summary.plannedAhead / stays : null;
    const missed = summary.plannedLate + summary.plannedWrong;
    const noPlan = stays - summary.everPlanned;
    return {
      facilityId: chip.facilityId,
      name: chip.name,
      stays,
      plannedAheadShare,
      plannedAheadLabel: shareLabel(plannedAheadShare),
      aheadPercent: percentOf(summary.plannedAhead, stays),
      missedPercent: percentOf(missed, stays),
      noPlanPercent: percentOf(noPlan, stays),
      barLabel: stays
        ? `${summary.plannedAhead} anticipated, ${missed} late or incorrect, ${noPlan} no plan`
        : 'No discharges in this window',
      averageNoticeDays: summary.averageNoticeDays,
      noticeLabel: summary.averageNoticeDays === null ? '—' : `${summary.averageNoticeDays}d`,
      surprises: summary.surprises,
      selected: selected.has(chip.facilityId),
    };
  });
}

const NAME = (a: string, b: string) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });

/** A building with nothing to compare (no stays, so no share) sorts last in
 *  both directions - the same blanks-last rule the table keeps. */
function byShare(a: number | null, b: number | null, dir: 1 | -1): number {
  if (a === null && b === null) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  return dir * (a - b);
}

/** Pure; exported for the tests. Ties fall back to the name, so two reads of
 *  one window agree on the order. */
export function sortCompareRows(rows: ReadonlyArray<FacilityCompareRow>, key: FacilityCompareKey, dir: 1 | -1): FacilityCompareRow[] {
  return [...rows].sort((a, b) => {
    switch (key) {
      case 'facility': return dir * NAME(a.name, b.name);
      case 'stays': return dir * (a.stays - b.stays) || NAME(a.name, b.name);
      case 'plannedAhead': return byShare(a.plannedAheadShare, b.plannedAheadShare, dir) || NAME(a.name, b.name);
      case 'notice': return byShare(a.averageNoticeDays, b.averageNoticeDays, dir) || NAME(a.name, b.name);
      case 'surprises': return dir * (a.surprises - b.surprises) || NAME(a.name, b.name);
      default: return 0;
    }
  });
}

@Injectable({ providedIn: 'root' })
export class DischargeAccuracyService {
  constructor(
    private server: ServerTokenService,
    private facilityService: FacilityService,
  ) { }

  /** The report for every facility the caller may see. The window is optional;
   *  the server applies its own default and answers with the one it used. */
  load(window: AccuracyWindow = {}): Observable<AccuracyReportView> {
    return this.registryReady().pipe(
      switchMap(() => this.server.postWithToken<AccuracyReportDto>('snfs/getDischargePlanAccuracy', window)),
      map(response => this.toView(response)),
    );
  }

  /** The registry, ready or timed out - never an error, see REGISTRY_TIMEOUT_MS. */
  private registryReady(): Observable<void> {
    return defer(() => from(this.facilityService.waitUntilReady())).pipe(
      timeout(REGISTRY_TIMEOUT_MS),
      map(() => undefined),
      catchError(() => of(undefined)),
    );
  }

  toView(response: AccuracyReportDto | null | undefined): AccuracyReportView {
    const summary = response?.summary ?? EMPTY_SUMMARY;
    return {
      from: fullDay(response?.from),
      to: fullDay(response?.to),
      historySince: fullDay(response?.historySince ?? null),
      floored: response?.floored === true,
      noticeCapDays: typeof response?.noticeCapDays === 'number' ? response.noticeCapDays : null,
      leadFigures: leadFiguresOf(summary),
      figures: figuresOf(summary),
      // Absent, not empty, when the server predates the split: the screen then
      // shows no chips and the server's own figures, rather than chips that
      // all read 0 over a table full of rows.
      facilities: Array.isArray(response?.facilities) ? this.chipsOf(response.facilities) : [],
      rows: (response?.rows ?? []).map(row => this.toRowView(row)),
      truncated: response?.truncated === true,
    };
  }

  /**
   * One chip per building the reader may see, in the registry's order, each
   * with its share of the window. A building with no discharge in the window
   * still gets a chip, reading 0: here an absence is a finding, not a gap. A
   * building the server answered for that the registry does not list is kept
   * too, after the rest, so the chips can never hide a row.
   */
  private chipsOf(facilities: AccuracyFacilityTotalsDto[]): AccuracyFacilityChip[] {
    const byId = new Map<number, AccuracyTotalsDto>();
    for (const facility of facilities) {
      // Normalised through the adder, so a count the server did not send - a
      // server behind this screen by one deploy - is a 0, never an undefined
      // that divides into "NaN%" on the strip.
      if (facility && typeof facility.facilityId === 'number') byId.set(facility.facilityId, mergeTotals([facility.totals]));
    }
    const roster = this.facilityService.getUserFacilityIds();
    const ids = [...new Set([...roster, ...byId.keys()])];
    return ids.map(facilityId => {
      const totals = byId.get(facilityId) ?? EMPTY_TOTALS;
      return { facilityId, name: this.facilityNameOf(facilityId), stays: totals.stays, totals };
    });
  }

  private toRowView(row: AccuracyRowDto): AccuracyRowView {
    const actual = row?.actualDischargeDate ?? null;
    const variance = row?.varianceDays ?? null;
    const changes = row?.changes ?? [];
    const lead = leadOf(row?.lead);
    const notice = lead === 'ahead' ? (row?.noticeDays ?? null) : null;
    return {
      // The stay, not the patient: a readmission is a second row, and two rows
      // sharing a key would collapse into one in the DOM. The discharge day is
      // part of it because the admission day can be unknown - the mirror holds
      // no admission_date for some stays - and two such stays of one patient
      // would otherwise share the key, and with it one row and one open log.
      key: `${row.facilityId}:${row.patientId}:${row.stayStartedOn ?? ''}:${actual ?? ''}`,
      patientId: row.patientId,
      patientName: row.patientName || '',
      facilityId: row.facilityId,
      facilityName: this.facilityNameOf(row.facilityId),
      trail: trailOf(row?.changes ?? []),
      cleared: row?.cleared === true,
      actualDischargeDate: actual,
      lastExpectedDate: row?.lastExpectedDate ?? null,
      admittedLabel: shortDay(row?.admissionDay),
      dischargedLabel: shortDay(actual),
      // The Eastern day the first date was written on - the audit column,
      // beside a verdict that was read off the board two days out.
      plannedOnLabel: row?.firstPlannedAt ? dayOfStamp(row.firstPlannedAt) : '',
      // The hand that documented the first date - the same event "Documented on"
      // stamps, so the two columns describe one act. Every later hand is in
      // the log under the row.
      setBy: changes.length
        ? (changes[0].by || '')
        : '',
      admissionDay: row?.admissionDay ?? null,
      firstPlannedAt: row?.firstPlannedAt ?? null,
      lengthOfStayDays: row?.lengthOfStayDays ?? null,
      outcome: outcomeOf(variance, actual),
      timesDateMoved: row?.timesDateMoved ?? 0,
      varianceDays: variance,
      varianceLabel: varianceLabelOf(variance),
      lead,
      leadLabel: leadLabelOf(lead, notice, row?.boardDate ?? null),
      noticeDays: notice,
      stayLabel: stayLabelOf(row?.lengthOfStayDays ?? null),
      changes: changeViewsOf(changes),
    };
  }

  /** The same helper and the same fallback the Discharge Plan screen uses, so
   *  one building is named identically on both. The id is the honest fallback:
   *  a registry that has not loaded yet must not print an empty cell where a
   *  building belongs. */
  private facilityNameOf(facilityId: number): string {
    return this.facilityService.getFacilityDisplayName(facilityId) || `Facility ${facilityId}`;
  }
}
